import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tooltip,
} from 'carbon-components-react';
import { useContext } from 'react';
import { Tree20 } from '@carbon/icons-react';
import { layoutContext } from '../FlashSustainabilityCalculator';
import calculatorVariables from '../../data/calculationData';
import './ComparisonTable.scss';

export interface comparisonTableProps {
  contents: {
    AverageTitle: JSX.Element;
    averageTooltip: JSX.Element;
    carbonOutputMetric: JSX.Element;
    comparisonVariables: {
      icon: JSX.Element;
      label: JSX.Element;
      toolTip: JSX.Element;
    }[];
    electricityCarbonLabel: JSX.Element;
    savedTreeLabel: JSX.Element;
    savedTreeLabelComplement: JSX.Element;
  };
}

const ComparisonTable = (ComparisonTableProps: comparisonTableProps) => {
  const { contents } = ComparisonTableProps;
  const { currentCountry, selectedOption } = useContext(layoutContext);

  /* 
    Function that receives the selectedOption props as params
    and then consult the calculatorVariables static data to 
    retrieve variables correpondent.  
  */
  const selectState = (
    usableStorage: number | undefined,
    performanceLevel: string | undefined,
  ) => {
    let modifiersVariables;
    /* Guarantee that function's params are not undefined */
    if (usableStorage && performanceLevel) {
      modifiersVariables = calculatorVariables[usableStorage][performanceLevel];
    }

    return modifiersVariables;
  };

  const modifiersVariables = selectState(
    selectedOption?.usableStorage,
    selectedOption?.performanceLevel,
  );

  let ibmPlatformName: string;

  switch (selectedOption?.performanceLevel) {
    case 'lite':
      ibmPlatformName = 'IBM FlashSystem 5045';
      break;
    case 'medium':
      ibmPlatformName = 'IBM FlashSystem 5200';
      break;
    case 'high':
      ibmPlatformName = 'IBM FlashSystem 7300';
      break;
    case 'extreme':
      ibmPlatformName = 'IBM FlashSystem 9500';
      break;
    default:
      ibmPlatformName = 'IBM FlashSystem 5200';
  }
  const carbonFactorDivisor = 1000000;
  const carbonOutputDivisor = 0.000364;
  return (
    <>
      {/* Main comparison table */}
      <Table
        className="comparison-table-wrapper"
        style={{ overflow: 'hidden' }}
      >
        <TableHead>
          <TableRow>
            <TableHeader></TableHeader>
            <TableHeader className="second"></TableHeader>
            <TableHeader className="competitor">
              {contents.AverageTitle}
              <Tooltip direction="bottom">
                <p>{contents.averageTooltip}</p>
              </Tooltip>
            </TableHeader>
            <TableHeader>
              <h4>{ibmPlatformName}</h4>
            </TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>

        <TableBody>
          {contents.comparisonVariables.map(
            (
              el: {
                icon: JSX.Element;
                label: JSX.Element;
                toolTip: JSX.Element;
              },
              i: number,
            ) => {
              return (
                <TableRow>
                  <TableCell>{el.icon}</TableCell>
                  <TableCell>{el.label}</TableCell>
                  <TableCell className="competitor">
                    {i === 0 && modifiersVariables?.compDrivesNumber}
                    {i === 1 && modifiersVariables?.compDriveInterface}
                    {i === 2 && modifiersVariables?.compRackUnits}
                    {i === 3 &&
                      /* Guarantee that modifiersVariables and currentCountry objects are not undefined */
                      modifiersVariables &&
                      currentCountry &&
                      /* Then calculate competitor's carbon output value based on current modifiersVariables */
                      (
                        (currentCountry?.carbonFactor / carbonFactorDivisor) *
                        (modifiersVariables?.compCarbonOutputVariable /
                          carbonOutputDivisor)
                      ).toFixed(1)}
                    &nbsp;
                    {
                      /* Data portion that requires translation */
                      i === contents.comparisonVariables.length - 1
                        ? contents.carbonOutputMetric
                        : ''
                    }
                  </TableCell>
                  <TableCell>
                    <label>
                      {i === 0 && modifiersVariables?.ibmDrivesNumber}
                      {i === 1 && modifiersVariables?.ibmdriveInterface}
                      {i === 2 && modifiersVariables?.ibmRackUnits}
                      {i === 3 &&
                        /* Guarantee that modifiersVariables and currentCountry objects are not undefined */
                        modifiersVariables &&
                        currentCountry &&
                        /* Then calculate IBM's carbon output value based on current modifiersVariables */
                        (
                          (currentCountry?.carbonFactor / carbonFactorDivisor) *
                          (modifiersVariables?.ibmCarbonOutputVariable /
                            carbonOutputDivisor)
                        ).toFixed(1)}
                      &nbsp;
                      {
                        /* Data portion that requires translation */
                        i === contents.comparisonVariables.length - 1
                          ? contents.carbonOutputMetric
                          : ''
                      }
                    </label>
                  </TableCell>
                  <TableCell className="info">
                    <Tooltip direction="left">
                      <p>{el.toolTip}</p>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            },
          )}
        </TableBody>
      </Table>
      {/* Resources saving table */}
      <Table className="bx--data-Table" style={{ width: '100%' }}>
        <TableBody>
          <TableRow className="total">
            <TableCell></TableCell>
            <TableCell>{contents.electricityCarbonLabel}</TableCell>
            <TableCell></TableCell>
            <TableCell className="electotal">
              <label className="electotal">
                {currentCountry?.currencySymbol} {''}
                {
                  /* Guarantee that modifiersVariables and currentCountry objects are not undefined */
                  modifiersVariables &&
                    currentCountry &&
                    /* Then calculate Total Savings value based on current modifiersVariables */
                    (
                      modifiersVariables?.totalSavingsVariable *
                      currentCountry?.exchangeRate *
                      (currentCountry?.electricity + 0.786)
                    )
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              </label>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
          <TableRow className="tree">
            <TableCell>
              <Tree20 />
            </TableCell>
            <TableCell>
              <div className="treetotal">
                {contents.savedTreeLabel}{' '}
                {
                  /* Guarantee that modifiersVariables object is not undefined 
                  and then fix the number on 0 using fixed-point notation */
                  modifiersVariables &&
                    (modifiersVariables?.savedTreesNumber).toFixed(0)
                }{' '}
                {contents.savedTreeLabelComplement}
              </div>
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ComparisonTable;
